import React, { useState, useRef, useEffect, useMemo } from "react";
import OrderCard from "./OrderCard";
import ProductListCard from "./ProductListCard";
import CategoryProductCard from "./CategoryProductCard";
import PaymentAccountCard from "./PaymentAccountCard";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { ReactionType, SenderType, type BlockContent, type CommandVariable, type MessageButton } from '../../../../types/chat-message';
import { RevenueReport } from '../CustomResponse/RevenueReport';
import { ProfitLossReport } from '../CustomResponse/ProfitLossReport';
import { IncomeExpenseReport } from '../CustomResponse/IncomeExpenseReport';
import { StockReport } from '../CustomResponse/StockReport';
import { ReportTitle } from '../CustomResponse/ReportTitle';
import { LikeIcon, DislikeIcon, CopyIcon, CopyIconSuccess, CopyIconSuccessCircle, QuoteIcon, ThinkingIcon, VolumeIcon, VolumeMuteIcon, CskhBotMsgIcon } from "@assets/icon";
import type { ChatMessage } from '../../../../types';
import { CopyToClipboard } from "react-copy-to-clipboard";
import CitationButton from "./CitationButton";
import FollowUpSuggestions from "./FollowUpSuggestions";
import ConfirmSwitchMessage from "./ConfirmSwitchMessage";
import { ComponentInfoChipFromPayload } from "./ComponentInfoChip";
import "./ChatAdmin.scss";
import { ChatAgentType } from "@constants/chat-agents";
import { stripMarkdown } from "@utils/textUtils";
import { formatChatTime } from "@utils/index";
import { openChatLink, isDeeplinkUrl, linkTextParts } from "@utils/linkUtils";
import { chatService } from "@services/chatService";
import { getButtonStyle } from "@utils/buttonUtils";
import { useTranslation } from "react-i18next";
import type { MessageGroupPosition } from "./conversationGroupUtils";
import DislikeBlock from "./DislikeBlock";
import { ToastMessage } from "@hooks/useChat";
import {
  alignBlockContentsWithFullContent,
  extractImageUrlsFromBlockContents,
  extractTextFromTextBlocksOnly,
  isImagesBlockType,
} from "@utils/chat-message";
import ImageGallery from "./ImageGallery";
import { formatCategoryProductCopy, parseCategoryProductBlocks } from './categoryProductUtils';
import { formatPaymentAccountCopy, parsePaymentAccountBlocks } from './paymentAccountUtils';

const normalizeMarkdownHref = (href?: string): string => {
  if (!href) return '#';
  const trimmed = href.trim();
  if (/^(?:https?:\/\/|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (/^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
};

const renderMarkdownLink = ({ href, children, ...props }: any) => {
  const safeHref = normalizeMarkdownHref(href);
  const isDeeplink = isDeeplinkUrl(safeHref);
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    if (isDeeplink) {
      e.preventDefault();
      openChatLink(safeHref);
    }
  };
  return (
    <a href={safeHref} target={isDeeplink ? undefined : '_blank'}
      rel={isDeeplink ? undefined : 'noopener noreferrer'} onClick={handleClick} {...props}>
      {children}
    </a>
  );
};

const renderCodeBlock = ({ inline, className, children, ...props }: any) => {
  const match = /language-(\w+)/.exec(className || "");
  if (!inline && match) {
    return (
      <SyntaxHighlighter {...props} style={materialDark} language={match[1]} PreTag="div">
        {String(children ?? '').replace(/\n$/, "")}
      </SyntaxHighlighter>
    );
  }
  return <code className={className} {...props}>{children}</code>;
};

const fixStreamingMarkdown = (text: string, isFinal?: boolean): string => {
  if (isFinal) return text;
  let res = text.replace(/\*+$/, '');
  const boldMatches = res.match(/\*\*/g) || [];
  if (boldMatches.length % 2 !== 0) {
    res += '**';
  }
  return res;
};

const cleanupMarkdownBlocks = (text: string, hasValidBlocks?: boolean): string => {
  if (!hasValidBlocks) return text;
  let res = text.replace(/(?<![\n\*])([ \t]*\*(?!\*)[ \t]+)/g, '\n\n$1');
  res = res.replace(/^\s*\*\*\*\s*$/gm, '');
  if (res.includes('|')) {
    res = res.split('|')[0];
  }
  return res.replace(/(?<!\n)\n(?!\n)/g, '\n\n');
};

const preprocessMarkdownText = (text: string, isFinal?: boolean, hasValidBlocks?: boolean): string => {
  const stripped = text.replace(/\|.*$/s, '');
  const streamed = fixStreamingMarkdown(stripped, isFinal);
  return cleanupMarkdownBlocks(streamed, hasValidBlocks);
};

const getMarkdownComponents = (isInline?: boolean) => ({
  p: ({ children }: any) => (isInline ? <>{children}</> : <p>{children}</p>),
  a: renderMarkdownLink,
  code: renderCodeBlock,
});

const renderMarkdownBody = (processed: string, isInline?: boolean) => {
  const Wrapper = isInline ? "span" : "div";
  return (
    <Wrapper className={`markdown-content ${isInline ? 'inline' : ''}`}>
      <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} components={getMarkdownComponents(isInline)}>
        {processed}
      </ReactMarkdown>
    </Wrapper>
  );
};

// Simplified runner using ReactMarkdown to handle all markdown features (###, **, etc.)
const renderFormattedText = (
  text: string,
  isInline?: boolean,
  isFinal?: boolean,
  hasValidBlocks?: boolean
): React.ReactNode => {
  if (!text) return null;
  const processed = preprocessMarkdownText(text, isFinal, hasValidBlocks);
  return renderMarkdownBody(processed, isInline);
};


const checkIsHidden = (commandVariables?: CommandVariable[]): boolean => {
  if (!commandVariables) return false;
  const hiddenVar = commandVariables.find(v => v.name === 'hidden_message');
  if (!hiddenVar) return false;
  return hiddenVar.value === 'true';
};

const getBlockByType = (blockContents: BlockContent[] | undefined, type: string): BlockContent | undefined =>
  blockContents?.find(b => b.type === type);

const getButtonsFromBlockContents = (blockContents: BlockContent[] | undefined): any[] => {
  const textBlock = blockContents?.find(b => b.type === 'TEXT' && (b.payload as any).buttons);
  return (textBlock?.payload as any)?.buttons || [];
};

const openCategoryProductLink = (url: string) => {
  openChatLink(url);
};

const renderLinkBlock = (payload: any, index: number, hasNext: boolean) => {
  const text = payload?.text || payload?.url || '';
  const url = payload?.url || '';
  const hasTaiDay = text.toLocaleLowerCase('vi').includes('tại đây');
  const [before, after] = hasTaiDay ? linkTextParts(text) : ['', ''];
  const paddingLeft = (payload?.level || 0) > 1 ? `${(payload.level || 0) * 16}px` : undefined;
  return (
    <div key={index} className="block-content-item" style={{ marginBottom: hasNext ? '8px' : '0', paddingLeft }}>
      <div className={`block-link${payload?.isBold ? ' block-link--bold' : ''}`}>
        {before && <span>{before}</span>}
        <button type="button" className="block-link__button" disabled={!url} onClick={() => openChatLink(url)}>
          {hasTaiDay ? 'tại đây' : (text || 'tại đây')}
        </button>
        {after && <span>{after}</span>}
      </div>
    </div>
  );
};

const getCskhAvatarAlignClass = (_pos?: MessageGroupPosition): string => {
  return 'items-end';
};

const extractMarkdownUrls = (text?: string): string[] => {
  if (!text) return [];
  const matches = text.matchAll(/!\[image\]\((.*?)\)/g);
  const urls: string[] = [];
  for (const match of matches) {
    if (match[1]?.trim()) urls.push(match[1].trim());
  }
  return urls;
};

const extractBlockImageUrls = (blocks?: BlockContent[]): string[] => {
  if (!blocks?.length) return [];
  const urls = extractImageUrlsFromBlockContents(blocks);
  blocks.forEach((block: any) => {
    if (block?.payload?.imageUrl?.trim()) urls.push(block.payload.imageUrl.trim());
    if (block?.payload?.text) urls.push(...extractMarkdownUrls(block.payload.text));
  });
  return urls;
};

const extractAllBotImageUrls = (
  blocks?: BlockContent[],
  text?: string,
  chatText?: string,
): string[] => {
  const combined = [
    ...extractBlockImageUrls(blocks),
    ...extractMarkdownUrls(text),
    ...extractMarkdownUrls(chatText),
  ];
  return Array.from(new Set(combined.filter(Boolean)));
};

const getCleanTextWithoutImages = (
  blocks?: BlockContent[],
  rawMessage?: string,
): string => {
  if (blocks?.length) {
    const textFromBlocks = extractTextFromTextBlocksOnly(blocks);
    const cleaned = textFromBlocks?.replace(/!\[image\]\(.*?\)/g, '')?.trim();
    if (cleaned) return cleaned;
  }
  return (rawMessage || '').replace(/!\[image\]\(.*?\)/g, '').trim();
};

interface ChatAdminProps {
  onQuestionClick?: (question: string) => void;
  messageId?: string;
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void | Promise<void>;
  onPaymentAccountConfirm?: (button: MessageButton) => void | Promise<void>;
  onThinkingStop?: () => void;
  chatMessage?: ChatMessage;
  message: string;
  handleAgentChange?: (agent: ChatAgentType) => void;
  isLastMessage?: boolean;
  handleForwardToAgent?: (targetAgent: ChatAgentType, userQuestion: string | undefined, botMessage: ChatMessage | undefined, botMessageId?: string) => void;
  handleRateMessage?: (messageId: string, action: 'LIKE' | 'DISLIKE', dislikeReason?: string, reasonIds?: string[]) => Promise<void>;
  onQuote?: (message: ChatMessage) => void;
  activeAgent?: ChatAgentType;
  onConfirmStay?: (messageId: string, blockContents?: import('../../../../types/chat-message').BlockContent[]) => void;
  onConfirmLeave?: (messageId: string, pendingAgent: ChatAgentType) => void;
  setToastMessage?: (toast: NonNullable<ToastMessage>) => void;
  audioData?: string;
  isAudioEnabled?: boolean;
  onToggleAudio?: () => void;
  onPlayAudio?: (audio: string) => void;
  onStopAudio?: () => void;
  isBotSpeaking?: boolean;
  isAudioPlayerVisible?: boolean;
  groupPosition?: MessageGroupPosition;
}


// ... imports

const ChatAdmin: React.FC<ChatAdminProps> = ({
  message,
  onQuestionClick,
  onButtonClick,
  onPaymentAccountConfirm,
  onThinkingStop,
  chatMessage,
  handleAgentChange,
  isLastMessage,
  handleRateMessage,
  onQuote,
  activeAgent,
  onConfirmStay,
  onConfirmLeave,
  setToastMessage,
  audioData,
  isAudioEnabled,
  onToggleAudio,
  onPlayAudio,
  onStopAudio,
  isBotSpeaking,
  isAudioPlayerVisible = false,
  groupPosition,
}) => {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState<ReactionType | null>(
    chatMessage?.actionMessage as ReactionType | null ?? null
  );
  const [showToastMessage, setShowToastMessage] = useState<boolean>(false);
  const [toastType, setToastType] = useState<string>("");
  const [showDislikeBlock, setShowDislikeBlock] = useState<boolean>(false);
  const [isVolumeActive, setIsVolumeActive] = useState(false);
  const volumeActivatedByClickRef = useRef(false);

  const isFromHistory = chatMessage?._isHistory === true;
  const isBypassAnimation = isFromHistory ||
    chatMessage?.skipAnimation === true ||
    activeAgent === ChatAgentType.CSKH ||
    chatMessage?.typeChat === ChatAgentType.CSKH ||
    chatMessage?.responseMode === 'single' ||
    chatMessage?.fullContent === "Nghiệp vụ chưa hỗ trợ trên trình duyệt, vui lòng thử lại trên app điện thoại" ||
    message === "Nghiệp vụ chưa hỗ trợ trên trình duyệt, vui lòng thử lại trên app điện thoại";

  // ─── Typewriter animation ──────────────────────────────────────────────────
  const [displayedMessage, setDisplayedMessage] = useState(isBypassAnimation ? message : "");
  const [isAnimating, setIsAnimating] = useState(false);
  const typedTextRef = useRef(isBypassAnimation ? message : "");
  const targetTextRef = useRef(message);
  const isAnimatingRef = useRef(false);
  const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const [isStopped, setIsStopped] = useState(chatMessage?.isStopped === true);
  const isStoppedRef = useRef(chatMessage?.isStopped === true);

  // Dispatch typewriter-animation-change event to inform parent components (like InputMessage)
  useEffect(() => {
    if (isLastMessage && !isBypassAnimation) {
      window.dispatchEvent(new CustomEvent('typewriter-animation-change', {
        detail: { isAnimating, messageId: chatMessage?.id || chatMessage?.messageId }
      }));
    }
    return () => {
      if (isLastMessage && !isBypassAnimation) {
        window.dispatchEvent(new CustomEvent('typewriter-animation-change', {
          detail: { isAnimating: false, messageId: chatMessage?.id || chatMessage?.messageId }
        }));
      }
    };
  }, [isAnimating, isLastMessage, chatMessage?.id, chatMessage?.messageId, isBypassAnimation]);

  // Listen to skip-typewriter-animation (Stop) event
  useEffect(() => {
    if (isBypassAnimation || !isLastMessage) return;

    const handleStop = () => {
      console.log('[Typewriter] Stop/Freeze animation event received');
      isStoppedRef.current = true;
      setIsStopped(true);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      isAnimatingRef.current = false;
      setIsAnimating(false);
    };

    window.addEventListener('skip-typewriter-animation', handleStop);
    return () => window.removeEventListener('skip-typewriter-animation', handleStop);
  }, [isBypassAnimation, isLastMessage]);

  useEffect(() => {
    if (chatMessage?.isStopped) {
      isStoppedRef.current = true;
      setIsStopped(true);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      isAnimatingRef.current = false;
      setIsAnimating(false);
      if (isBypassAnimation) {
        setDisplayedMessage(message);
        typedTextRef.current = message;
      }
    } else {
      isStoppedRef.current = false;
      setIsStopped(false);
    }
  }, [chatMessage?.isStopped, message, isBypassAnimation]);

  useEffect(() => {
    // Nếu đã dừng stream, không cập nhật gì thêm cho text hiển thị
    if (isStoppedRef.current) {
      return;
    }

    targetTextRef.current = message;

    // Reset/Sync ngay lập tức nếu là tin nhắn từ lịch sử hoặc cần bypass animation
    if (isBypassAnimation) {
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      typedTextRef.current = message;
      setDisplayedMessage(message);
      isAnimatingRef.current = false;
      setIsAnimating(false);
      return;
    }

    // Reset animation khi đổi sang tin nhắn hoàn toàn khác
    if (message.length > 0 && typedTextRef.current.length > 0 && !message.startsWith(typedTextRef.current)) {
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      typedTextRef.current = message;
      setDisplayedMessage(message);
      isAnimatingRef.current = false;
      setIsAnimating(false);
      isStoppedRef.current = false;
      setIsStopped(false);
      return;
    }

    // Nếu chưa chạy animation và độ dài text hiển thị ngắn hơn target, bắt đầu loop
    if (!isAnimatingRef.current && typedTextRef.current.length < message.length) {
      isAnimatingRef.current = true;
      setIsAnimating(true);

      const CHAR_DELAY_MS = 21

      const tick = () => {
        const currentTarget = targetTextRef.current;
        const currentTyped = typedTextRef.current;

        if (currentTyped.length < currentTarget.length) {
          const gap = currentTarget.length - currentTyped.length;
          // Tăng tốc gõ nếu khoảng cách trễ quá xa
          const step = Math.min(gap > 100 ? 10 : gap > 50 ? 6 : 3, gap);
          const nextText = currentTarget.slice(0, currentTyped.length + step);
          typedTextRef.current = nextText;
          setDisplayedMessage(nextText);
          animTimerRef.current = setTimeout(tick, CHAR_DELAY_MS);
        } else {
          isAnimatingRef.current = false;
          setIsAnimating(false);
        }
      };

      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      tick();
    }
  }, [message, isBypassAnimation]);

  // Sync displayedMessage khi messageId thay đổi (chuyển sang message khác hoàn toàn)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Nếu tin nhắn đang stream (chưa final) và không phải từ lịch sử, không force-sync để tránh làm gián đoạn animation khi đổi messageId tạm -> thật
    if (chatMessage?.isFinal !== true && !chatMessage?._isHistory) {
      return;
    }

    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    typedTextRef.current = message;
    setDisplayedMessage(message);
    isAnimatingRef.current = false;
    setIsAnimating(false);
  }, [chatMessage?.messageId, chatMessage?.id]);
  // ──────────────────────────────────────────────────────────────────────────

  const messageContentRef = useRef<HTMLDivElement>(null);

  // Reset feedback when message or agent changes - restore from actionMessage
  useEffect(() => {
    setFeedback(chatMessage?.actionMessage as ReactionType | null ?? null);
  }, [chatMessage?.messageId, chatMessage?.agentType]);

  // Auto hide toast after 2 seconds
  useEffect(() => {
    if (showToastMessage) {
      const timer = setTimeout(() => {
        setShowToastMessage(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showToastMessage]);

  // Peach on volume while this session is playing/paused; clear when audio ends or player closes
  useEffect(() => {
    if (isAudioPlayerVisible) {
      if (isBotSpeaking && isLastMessage && !volumeActivatedByClickRef.current) {
        setIsVolumeActive(true);
      }
      return;
    }
    setIsVolumeActive(false);
    volumeActivatedByClickRef.current = false;
  }, [isAudioPlayerVisible, isBotSpeaking, isLastMessage]);

  // TTS: gửi audioRawText lên chat-service để lấy base64 rồi phát loa (cache theo tin nhắn)
  const ttsAudioRef = useRef<string | null>(null);
  const [isTtsLoading, setIsTtsLoading] = useState(false);

  const fetchTtsAudio = async (): Promise<string | null> => {
    if (ttsAudioRef.current) return ttsAudioRef.current;
    const text = (chatMessage?.audioRawText || '').trim() || stripMarkdown(message || '').trim();
    const token = sessionStorage.getItem('access_token');
    if (!text || !token) return null;

    setIsTtsLoading(true);
    try {
      const audio = await chatService.convertTextToSpeech(token, text);
      if (audio) ttsAudioRef.current = audio;
      return audio;
    } finally {
      setIsTtsLoading(false);
    }
  };

  const stopActiveVolume = () => {
    onStopAudio?.();
    setIsVolumeActive(false);
    volumeActivatedByClickRef.current = false;
  };

  const startVolumePlayback = (audio: string) => {
    onStopAudio?.();
    volumeActivatedByClickRef.current = true;
    setIsVolumeActive(true);
    onPlayAudio?.(audio);
  };

  const handleVolumeClick = async () => {
    if (isVolumeActive) {
      stopActiveVolume();
      return;
    }

    if (isTtsLoading) return;

    // Ưu tiên audio từ API TTS; lỗi thì dùng audio có sẵn của tin nhắn (socket)
    const audio = (await fetchTtsAudio()) || audioData || chatMessage?.audio;
    if (audio) {
      startVolumePlayback(audio);
    }
  };

  const handleQuestionClick = (question: string) => {
    if (onQuestionClick) {
      onQuestionClick(question);
    }
  };

  const getToastMessage = (): string => {
    return toastType === "COPY" ? "common.copySuccess" : "common.likeAndDislikeMessage";
  };

  const handleCopySuccess = () => {
    setShowToastMessage(true);
    setToastType("COPY");
  };

  // change to handleReaction, and pass prop like, dislike
  const handleReaction = (reaction: ReactionType) => {
    if (feedback === reaction) return;
    setFeedback(reaction);
    setToastType(reaction);

    if (reaction === ReactionType.DISLIKE) {
      setShowDislikeBlock(true);
    } else {
      setShowToastMessage(true);
    }

    if (chatMessage?.id && handleRateMessage) {
      handleRateMessage(chatMessage.id, reaction);
    }
  }

  const handleDislikeBlockClose = () => {
    setShowDislikeBlock(false);
  };

  const handleDislikeSubmitReason = (reason: string, reasonIds: string[]) => {
    if (chatMessage?.id && handleRateMessage) {
      handleRateMessage(chatMessage.id, ReactionType.DISLIKE, reason, reasonIds);
    }
  };

  const handleShowDislikeToast = () => {
    setShowToastMessage(true);
  };

  const isOrderMessage = chatMessage?.blockContents?.some(b => b.type === 'ORDER_INFO');
  const isProductListMessage = chatMessage?.blockContents?.some(b => b.type === 'PRODUCT_LIST');
  const categoryProduct = parseCategoryProductBlocks(chatMessage?.intent, chatMessage?.blockContents);
  const paymentAccount = parsePaymentAccountBlocks(chatMessage?.intent, chatMessage?.blockContents);
  const isCategoryProductMessage = categoryProduct !== null;
  const isCompleteMessage = chatMessage?.isMsgHasBg;
  const isChangeFlowMessage = chatMessage?.intent === 'change_flow';

  const REPORT_BLOCK_TYPES = ['REVENUE_REPORT', 'PROFIT_LOSS_REPORT', 'INCOME_EXPENSE_REPORT', 'STOCK_REPORT'] as const;
  type ReportBlockType = typeof REPORT_BLOCK_TYPES[number];

  const activeReportType = chatMessage?.blockContents
    ?.find(b => REPORT_BLOCK_TYPES.includes(b.type as ReportBlockType))?.type as ReportBlockType | undefined;

  const isReportMessage = !!activeReportType;

  const isSpecialCard = isOrderMessage || isProductListMessage || isCategoryProductMessage || !!paymentAccount || isReportMessage || isCompleteMessage;
  const isCskhMode =
    activeAgent === ChatAgentType.CSKH ||
    chatMessage?.typeChat === ChatAgentType.CSKH;
  const showCskhBubble = isCskhMode && !isSpecialCard;
  const isTypingComplete =
    isFromHistory ||
    !!isSpecialCard ||
    (isStopped && isBypassAnimation) ||
    (chatMessage?.isFinal === true && !isAnimating && displayedMessage.length >= message.length);

  const rawBotTime = chatMessage?.createdAt || chatMessage?.time;
  const formattedTime = formatChatTime(rawBotTime || new Date().toISOString());
  const hasTextContent = Boolean(message && message.replace(/!\[.*?\]\(.*?\)/g, '').trim().length > 0);
  const hasBlockTextContent = Boolean(
    chatMessage?.blockContents?.some(
      (block: any) => block.type === 'TEXT' && block.payload?.text?.replace(/!\[.*?\]\(.*?\)/g, '').trim()
    )
  );
  const hasButtons = chatMessage?.buttons && chatMessage.buttons.length > 0;
  const blockImageUrls = extractImageUrlsFromBlockContents(chatMessage?.blockContents);
  const hasImage =
    blockImageUrls.length > 0 ||
    !!chatMessage?.blockContents?.some((block: any) => block.payload?.imageUrl) ||
    /!\[image\]\(/i.test(message || '');
  const hasValidText = Boolean(hasTextContent || hasBlockTextContent || isSpecialCard);
  const shouldShowTimeTooltip = Boolean(isCskhMode && formattedTime);
  const hasMessageContent = !!(hasTextContent || hasBlockTextContent || hasButtons || isSpecialCard || hasImage);

  const galleryImageUrls = useMemo(
    () => extractAllBotImageUrls(chatMessage?.blockContents, message, chatMessage?.text),
    [chatMessage?.blockContents, message, chatMessage?.text],
  );

  const textWithoutImages = useMemo(
    () => getCleanTextWithoutImages(chatMessage?.blockContents, message),
    [chatMessage?.blockContents, message],
  );

  const hasTextBubble = useMemo(() => {
    if (isSpecialCard) return true;
    return Boolean(textWithoutImages.trim());
  }, [isSpecialCard, textWithoutImages]);

  const isImageOnly = useMemo(() => {
    return galleryImageUrls.length > 0 && !hasTextBubble;
  }, [galleryImageUrls.length, hasTextBubble]);

  const getCopyText = () => {

    if (categoryProduct) {
      return stripMarkdown(formatCategoryProductCopy(categoryProduct));
    }

    if (paymentAccount) {
      return stripMarkdown(formatPaymentAccountCopy(paymentAccount));
    }

    if (isOrderMessage && chatMessage?.blockContents) {
      const titleBlock = chatMessage.blockContents.find(b => b.type === 'TEXT' && (b.payload as any).text?.toLowerCase().includes('thông tin đơn hàng'));
      const statusBlock = chatMessage.blockContents.find(b => b.type === 'TEXT' && b !== titleBlock && !(b.payload as any).buttons);
      const buttonBlock = chatMessage.blockContents.find(b => b.type === 'TEXT' && (b.payload as any).buttons && (b.payload as any).buttons!.length > 0);
      const orderInfoBlock = chatMessage.blockContents.find(b => b.type === 'ORDER_INFO');

      const buttons = (buttonBlock?.payload as any)?.buttons || [];
      const paymentButtons = buttons.filter((btn: any) =>
        btn.commandVariables?.some((v: any) => v.name === 'payment_method')
      );

      const statusText = (statusBlock?.payload as any)?.text || "Tạo đơn hàng thành công!";
      const titleText = (titleBlock?.payload as any)?.text || "THÔNG TIN ĐƠN HÀNG";
      const orderStatus = (titleBlock?.payload as any)?.status;

      const products = (orderInfoBlock?.payload as any)?.products || [];
      let productLine = "";
      if (products.length > 0) {
        const productNames = products.map((p: any) => `${p.quantity === '1' ? '' : p.quantity + ' '}${p.name}`).join(', ');
        const totalAmount = products[0].totalAmount || '0';
        productLine = `${productNames} ${totalAmount}`;
      } else {
        productLine = "Tổng tiền 0";
      }

      const paymentMethod = (buttonBlock?.payload as any)?.paymentMethod;
      const paymentNamesText = paymentButtons.map((b: any) => b.name).join(' ');
      const paymentName = paymentMethod || paymentNamesText;
      const paymentLine = paymentName ? `Thanh toán ${paymentName}` : "Thanh toán";

      return [
        statusText.replace("!", "") + "!",
        titleText,
        orderStatus,
        productLine,
        paymentLine,
      ].filter(Boolean).map(text => stripMarkdown(text)).join('\n');
    }

    if (isProductListMessage && chatMessage?.blockContents) {
      const textBlock = chatMessage.blockContents.find(b => b.type === 'TEXT');
      const textPayload = textBlock?.payload as any;
      const titleText = textPayload?.text || "";

      const listBlock = chatMessage.blockContents.find(b => b.type === 'PRODUCT_LIST');
      const listPayload = listBlock?.payload as any;
      const products = listPayload?.products || [];

      const productsText = products.map((product: any) => {
        const isQuantityZero = product.quantity === 0 || product.quantity === '0';
        const hasValidQuantity = product.quantity !== null && product.quantity !== undefined && product.quantity !== '' && !isQuantityZero && product.quantity !== 'Hết hàng';
        const isOutOfStock = product.quantity === 'Hết hàng' || isQuantityZero || product.quantity === null || (product.addToCart === false && !hasValidQuantity);

        let displayQuantity = product.quantity ?? '';

        if (product.quantity === 'Hết hàng') {
          displayQuantity = '0';
        } else if (isOutOfStock) {
          displayQuantity = 'Hết hàng';
        }

        const nameLine = product.name || "";
        const codePart = product.productCode ? `${product.productCode} ` : "";
        const quantityPart = displayQuantity !== '' ? `Tồn kho: ${displayQuantity}` : "";
        const codeAndQtyLine = (codePart + quantityPart).trim();
        const categoryLine = `Phân loại: ${product.category || "-"} ${product.price || ""}`.trim();

        return [nameLine, codeAndQtyLine, categoryLine].filter(Boolean).join('\n');
      }).join('\n\n');

      return [stripMarkdown(titleText), productsText].filter(Boolean).join('\n\n');
    }

    if (isReportMessage && chatMessage?.blockContents) {
      const reportBlock = getBlockByType(chatMessage.blockContents, activeReportType!);
      const data = (reportBlock?.payload as any)?.data;

      const fmtCurrency = (v: number) =>
        new Intl.NumberFormat('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v) + 'đ';
      const fmtRate = (r?: number) =>
        r !== undefined ? ` ${r > 0 ? '▲' : r < 0 ? '▼' : ''} ${Math.abs(r).toFixed(2)}%` : '';

      if (activeReportType === 'INCOME_EXPENSE_REPORT' && data) {
        const { timeRange, totalCashIn, totalCashOut, remindCash } = data;
        return [
          `BÁO CÁO THU CHI ${timeRange || ''}`.trim(),
          `Tổng thu: ${fmtCurrency(totalCashIn)}`,
          `Tổng chi: ${fmtCurrency(totalCashOut)}`,
          `Chênh lệch: ${remindCash >= 0 ? '+' : ''}${fmtCurrency(remindCash)}`,
        ].join('\n');
      }

      if (activeReportType === 'PROFIT_LOSS_REPORT' && data) {
        const { timeRange, totalRevenue, totalCost, totalProfit, totalProfitOther,
          totalRevenueRate, totalCostRate, totalProfitRate, totalProfitOtherRate } = data;
        return [
          `Báo cáo lãi lỗ ${timeRange || ''}`.trim(),
          `Lợi nhuận: ${fmtRate(totalProfitRate)}  ${fmtCurrency(totalProfit)}`,
          `Doanh thu: ${fmtRate(totalRevenueRate)}  ${fmtCurrency(totalRevenue)}`,
          `Giá vốn: ${fmtRate(totalCostRate)}   ${fmtCurrency(totalCost)}`,
          `Lợi nhuận khác: ${fmtRate(totalProfitOtherRate)}  ${fmtCurrency(totalProfitOther)}`,
        ].join('\n');
      }

      if (activeReportType === 'REVENUE_REPORT' && data) {
        const { startDate, endDate, revenue, orders, avgOrder, topProducts = [] } = data;
        const dateLabel = startDate === endDate ? startDate : `${startDate} - ${endDate}`;

        const titleBlock = chatMessage.blockContents.find((b: any) => b.type === 'TEXT');
        const title = (titleBlock?.payload as any)?.text || `DOANH THU BÁN HÀNG ${dateLabel || ''}`.trim();

        const fmtMetric = (label: string, metric?: any) => {
          if (!metric) return null;
          const dir = metric.direction === 'up' ? '▲' : metric.direction === 'down' ? '▼' : '';
          const rateStr = dir ? `${dir}${(metric.rate ?? 0).toFixed(2)}%` : '';
          const valueStr = (metric.formattedValue ?? '').replace(' VND', ' đ');
          return rateStr ? `${label} ${rateStr}   ${valueStr}` : `${label}   ${valueStr}`;
        };

        const lines: string[] = [
          title,
          fmtMetric('Doanh thu thuần', revenue),
          fmtMetric('Đơn hàng', orders),
          fmtMetric('Trung bình/đơn', avgOrder),
        ].filter(Boolean) as string[];

        if (topProducts.length > 0) {
          lines.push('Hàng hóa bán chạy');
          topProducts.forEach((p: any) => {
            lines.push(`${p.name}   ${p.quantity} sản phẩm`);
          });
        }

        return lines.join('\n');
      }

      if (activeReportType === 'STOCK_REPORT' && data) {
        const { date, totalQuantity = 0, totalAmount = 0, totalOutOfStockQuantity = 0, totalCanSellQuantity = 0 } = data;

        const titleBlock = chatMessage.blockContents.find((b: any) => b.type === 'TEXT');
        const title = (titleBlock?.payload as any)?.text || (date ? `BÁO CÁO KHO HÔM NAY ${date}` : 'BÁO CÁO KHO HÀNG');

        const fmtNumber = (n: number) => new Intl.NumberFormat('vi-VN').format(n);
        const floor = Math.floor(totalQuantity);
        const decimal = totalQuantity % 1;
        const quantityStr = decimal !== 0
          ? `${fmtNumber(floor)},${Math.round(decimal * 100)} sản phẩm`
          : `${fmtNumber(floor)} sản phẩm`;

        return [
          title,
          `Số lượng: ${quantityStr}`,
          `Giá trị tồn: ${fmtCurrency(totalAmount)}`,
          `Đã hết hàng: ${totalOutOfStockQuantity} sản phẩm`,
          `Sản phẩm còn bán: ${totalCanSellQuantity} sản phẩm`,
        ].join('\n');
      }
    }

    const rawText = isStopped ? displayedMessage : message;
    const cleanRawText = (rawText || '').replace(/!\[.*?\]\(.*?\)/g, '');
    return stripMarkdown(cleanRawText);
  };

  const handleCompleteButtonClick = async (button: any) => {
    // Đợi tin nhắn "Không"/"Có" được gửi xong qua socket của agent hiện tại
    // trước khi chuyển agent, tránh race condition khiến tin nhắn bị gửi
    // nhầm sang socket của targetAgent (do handleAgentChange đóng socket cũ).
    await onButtonClick?.(button.name, button.id, checkIsHidden(button.commandVariables));

    // Xử lý chuyển agent khi user nhấn "Không" trên flow CHANGE_MODE_PENDING
    if (
      chatMessage?.typeChangeFlow === 'CHANGE_MODE_PENDING' &&
      chatMessage?.targetAgent &&
      button.intent === 'change_flow_confirm'
    ) {
      const confirmVar = button.commandVariables?.find((v: any) => v.name === 'confirm');
      if (confirmVar?.value === 'false') {
        handleAgentChange?.(chatMessage.targetAgent as ChatAgentType);
      }
    }
  };

  // Tin hỏi tiếp tục luồng dang dở (vd. bán hàng nhanh): chữ thường + nút pill viền, căn trái
  const renderChangeFlowBlock = () => {
    const buttons = chatMessage?.buttons?.length
      ? chatMessage.buttons
      : getButtonsFromBlockContents(chatMessage?.blockContents);
    return (
      <div className="change-flow-block">
        <div className="block-text">
          {renderFormattedText(chatMessage?.text || '', false, true, true)}
        </div>
        {buttons.length > 0 && (
          <div className="change-flow-block__buttons">
            {buttons.map((button: any, index: number) => {
              const isDisabled = button.enabledColor === null || isFromHistory || !isLastMessage;
              return (
                <button
                  key={button.id || index}
                  type="button"
                  className={`change-flow-btn${isDisabled ? ' is-disabled' : ''}`}
                  disabled={isDisabled}
                  onClick={() => handleCompleteButtonClick(button)}
                  title={isFromHistory ? 'Tin nhắn từ lịch sử' : ''}
                >
                  {button.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCompleteBlock = () => {
    const text = chatMessage?.text || '';
    return (
      <div className="bg-white p-3 rounded-xl">
        <div className="block-contents">
          <div className="block-content-item">
            <div className="block-text">
              {renderFormattedText(text, false, chatMessage?.isFinal !== false)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-3 mt-3">
          {chatMessage?.buttons?.map((button: any, index: number) => {
            const isDisabled = button.enabledColor === null || isFromHistory || !isLastMessage;

            const baseClassName = 'px-4 py-1.5 rounded-[8px] border text-sm font-medium shadow-sm';
            const hoverClassName = isDisabled ? '' : 'hover:opacity-80';

            return (
              <button
                key={button.id || index}
                className={`${baseClassName} ${hoverClassName}`}
                style={{
                  transition: isDisabled ? 'none' : 'all 0.3s ease',
                  cursor: isDisabled ? 'default' : 'pointer',
                  borderColor: isDisabled ? '#E7EAF3B2' : 'var(--colour-neutral-surface)',
                  color: isDisabled ? '#b1b1b1ff' : 'var(--colour-primary-shade-md)',
                  backgroundColor: isDisabled ? '#E7EAF3B2' : 'var(--colour-primary-light-xxl)',
                  ...getButtonStyle(button, isDisabled),
                }}
              onClick={() => {
                  if (isDisabled) return;
                  void handleCompleteButtonClick(button);
                }}
                type="button"
                title={isFromHistory ? 'Tin nhắn từ lịch sử' : ''}
              >
                {button.name}
              </button>
            );
          })}
        </div>
      </div>
    )
  }

  const renderReport = (): React.ReactNode => {

    if (!activeReportType || !chatMessage?.blockContents) return null;
    const reportBlock = getBlockByType(chatMessage.blockContents, activeReportType);
    const payload = reportBlock?.payload as any;
    // if (!payload?.data) return null;

    const buttons = getButtonsFromBlockContents(chatMessage.blockContents) || chatMessage?.buttons;
    const isReportResponseComplete = isLastMessage && !chatMessage?._isHistory && chatMessage?.isFinal !== false;
    const commonProps = { onButtonClick, isLastMessage: isReportResponseComplete, isFromHistory: chatMessage?._isHistory };

    const sortedBlocks = [...chatMessage.blockContents].sort((a, b) => ((a.payload as any)?.order ?? 0) - ((b.payload as any)?.order ?? 0));
    const firstTextBlock = sortedBlocks.find(b => b.type === 'TEXT' && (b.payload as any)?.text && !(b.payload as any)?.buttons);
    const firstTextBlockText = (firstTextBlock?.payload as any)?.text as string | undefined;

    switch (activeReportType) {
      case 'REVENUE_REPORT':
        return <RevenueReport data={payload.data} buttons={buttons} {...commonProps} text={firstTextBlockText || payload.text} />;
      case 'PROFIT_LOSS_REPORT':
        return <ProfitLossReport data={payload.data} buttons={buttons} {...commonProps} text={firstTextBlockText || payload.text} />;
      case 'INCOME_EXPENSE_REPORT':
        return <IncomeExpenseReport data={payload.data} buttons={buttons} {...commonProps} text={firstTextBlockText || payload.text} />;
      case 'STOCK_REPORT':
        return <StockReport data={payload.data} buttons={buttons} {...commonProps} text={firstTextBlockText || payload.text} />;
      default:
        return null;
    }
  };

  // Render blockContents if available
  const renderContent = () => {
    if (isOrderMessage && chatMessage?.blockContents) {
      return <OrderCard blockContents={chatMessage.blockContents} onButtonClick={onButtonClick} onThinkingStop={onThinkingStop} isLastMessage={isLastMessage} chatMessageTime={chatMessage.createdAt || chatMessage.time} isFromHistory={chatMessage?._isHistory} setToastMessage={setToastMessage} />;
    }

    if (categoryProduct) {
      const canInteract = !!isLastMessage && !isFromHistory && !isStopped && chatMessage?.isFinal === true;
      return <CategoryProductCard data={categoryProduct} canInteract={canInteract}
        onButtonClick={onButtonClick} onLinkClick={openCategoryProductLink} />;
    }

    if (paymentAccount) {
      const canInteract = !!isLastMessage && !isFromHistory && !isStopped && chatMessage?.isFinal === true;
      return <PaymentAccountCard data={paymentAccount} canInteract={canInteract}
        onConfirm={onPaymentAccountConfirm} onLinkClick={openCategoryProductLink} />;
    }

    if (isProductListMessage && chatMessage?.blockContents) {
      return <ProductListCard blockContents={chatMessage.blockContents} onButtonClick={onButtonClick} onThinkingStop={onThinkingStop} isLastMessage={isLastMessage} isFromHistory={chatMessage?._isHistory} messageId={chatMessage?.messageId || chatMessage?.id} messageIdStr={chatMessage?.messageIdStr} />;
    }

    if (isReportMessage) {
      const rendered = renderReport();
      if (rendered) return rendered;
    }

    if (isChangeFlowMessage) {
      return renderChangeFlowBlock();
    }

    if (isCompleteMessage && chatMessage?.blockContents) {
      return renderCompleteBlock();
    }


    // Dùng displayedMessage để render text đang animate
    const displayStyleText = displayedMessage;
    const isFinal = chatMessage?.isFinal === true;

    const hasValidBlocks =
      chatMessage?.blockContents &&
      chatMessage.blockContents.length > 0 &&
      chatMessage.blockContents.some((b: any) =>
        (b.type === 'TEXT' && b.payload?.text) || b.payload?.imageUrl || b.type !== 'TEXT'
      );

    if (!isFinal || !hasValidBlocks || !isTypingComplete) {
      const cleanDisplayText = galleryImageUrls.length > 0
        ? displayStyleText.replace(/!\[image\]\(.*?\)/g, '').trim()
        : displayStyleText;
      if (!cleanDisplayText) return null;
      return (
        <span style={{ display: 'block' }}>
          {renderFormattedText(cleanDisplayText, false, true, true)}
        </span>
      );
    }

    // Sort blocks by order
    const sortedBlocks = [...chatMessage.blockContents].sort((a, b) => {
      const orderA = a.payload?.order ?? 0;
      const orderB = b.payload?.order ?? 0;
      return orderA - orderB;
    });

    // Render blockContents
    return (
      <div className="block-contents">
        {sortedBlocks.map((block: BlockContent, index: number) => {
          const payload = block.payload as any;

          if (isImagesBlockType(block) || (payload?.imageUrl && !payload?.text?.trim())) {
            return null;
          }

          if (block.type === 'COMPONENT_INFO') {
            return (
              <div
                key={index}
                className="block-content-item"
                style={{ marginBottom: index < sortedBlocks.length - 1 ? '8px' : '0' }}
              >
                <ComponentInfoChipFromPayload payload={payload} />
              </div>
            );
          }

          if (block.type?.toUpperCase() === 'LINK') {
            return renderLinkBlock(payload, index, index < sortedBlocks.length - 1);
          }

          if (block.type !== 'TEXT' || !payload?.text) {
            return null;
          }

          const textWithoutImages = String(payload.text)
            .replace(/!\[image\]\(.*?\)/g, '')
            .trim();
          if (!textWithoutImages) return null;

          const { isBold, level = 0 } = payload;
          const paddingLeft = level * 16;

          return (
            <div key={index} className="block-content-item" style={level > 1 ? { paddingLeft: `${paddingLeft}px` } : {}}>
              <div
                className={`block-text${isBold ? ' block-text--bold' : ''}`}
                style={{
                  fontWeight: isBold ? 600 : 400,
                  marginBottom: index < sortedBlocks.length - 1 ? '8px' : '0',
                }}
              >
                {isBold ? (
                  <ReportTitle text={textWithoutImages} className="" />
                ) : (
                  renderFormattedText(textWithoutImages, false, true, true)
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  const renderCopyButton = () => (
    <CopyToClipboard text={getCopyText()} onCopy={handleCopySuccess}>
      <button
        className="chat-action-btn chat-action-btn-copy"
        aria-label="Sao chép văn bản"
        type="button"
        data-tooltip-id="chatbot-tooltip"
        data-tooltip-content="Sao chép văn bản"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        {showToastMessage && toastType === "COPY" ? <CopyIconSuccess /> : <CopyIcon />}
      </button>
    </CopyToClipboard>
  );

  const renderQuoteButton = () => (
    <button
      className="chat-action-btn chat-action-btn-quote"
      aria-label="Trả lời"
      type="button"
      data-tooltip-id="chatbot-tooltip"
      data-tooltip-content="Trả lời"
      onClick={() => {
        if (chatMessage && onQuote) onQuote(chatMessage);
      }}
    >
      <QuoteIcon />
    </button>
  );

  const renderVolumeButton = () => (
    <button
      className={`chat-action-btn chat-action-btn-volume ${isVolumeActive ? 'active' : ''} ${isTtsLoading ? 'is-loading' : ''}`}
      disabled={isTtsLoading}
      aria-busy={isTtsLoading}
      onClick={handleVolumeClick}
      aria-label={isVolumeActive ? 'Dừng phát' : 'Đọc to'}
      type="button"
      data-tooltip-id="chatbot-tooltip"
      data-tooltip-content={isVolumeActive ? 'Dừng phát' : 'Đọc to'}
    >
      {(isAudioEnabled && !isVolumeActive) || isVolumeActive ? <VolumeIcon /> : <VolumeMuteIcon />}
    </button>
  );

  const renderLikeButton = () => (
    <button
      className={`chat-action-btn chat-action-btn-like ${feedback === ReactionType.LIKE ? 'active' : ''}`}
      onClick={() => handleReaction(ReactionType.LIKE)}
      aria-label="Trả lời tốt"
      type="button"
      data-tooltip-id="chatbot-tooltip"
      data-tooltip-content="Trả lời tốt"
      disabled={feedback === ReactionType.DISLIKE}
      style={{ display: feedback === ReactionType.DISLIKE ? 'none' : 'flex', cursor: feedback === ReactionType.LIKE ? 'default' : 'pointer' }}
    >
      <LikeIcon />
    </button>
  );

  const renderDislikeButton = () => (
    <button
      className={`chat-action-btn chat-action-btn-dislike ${feedback === ReactionType.DISLIKE ? 'active' : ''}`}
      onClick={() => handleReaction(ReactionType.DISLIKE)}
      aria-label="Trả lời tệ"
      type="button"
      data-tooltip-id="chatbot-tooltip"
      data-tooltip-content="Trả lời tệ"
      disabled={feedback === ReactionType.LIKE}
      style={{ display: feedback === ReactionType.DISLIKE ? 'none' : 'flex', cursor: feedback === ReactionType.DISLIKE ? 'default' : 'pointer' }}
    >
      <DislikeIcon />
    </button>
  );

  const renderCskhCopyBtn = () => (
    <CopyToClipboard text={getCopyText()} onCopy={handleCopySuccess}>
      <button
        className="chat-action-btn chat-action-btn-copy flex h-8 w-8 items-center justify-center rounded-full border-0 bg-[#F2F2F4] text-[#242428] transition-opacity hover:opacity-80 [&>svg]:h-4 [&>svg]:w-4"
        aria-label="Sao chép văn bản"
        type="button"
        data-tooltip-id="chatbot-tooltip"
        data-tooltip-content="Sao chép văn bản"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        {showToastMessage && toastType === "COPY" ? <CopyIconSuccess /> : <CopyIcon />}
      </button>
    </CopyToClipboard>
  );

  const renderCskhQuoteBtn = () => (
    <button
      className="chat-action-btn chat-action-btn-quote flex h-8 w-8 items-center justify-center rounded-full border-0 bg-[#F2F2F4] text-[#242428] transition-opacity hover:opacity-80 [&>svg]:h-4 [&>svg]:w-4"
      aria-label="Trả lời"
      type="button"
      data-tooltip-id="chatbot-tooltip"
      data-tooltip-content="Trả lời"
      onClick={() => {
        if (chatMessage && onQuote) onQuote(chatMessage);
      }}
    >
      <QuoteIcon />
    </button>
  );

  const renderCskhActions = () => (
    <div className="chat-admin-actions chat-admin-actions--cskh flex shrink-0 items-center gap-1">
      {renderCskhCopyBtn()}
      {renderCskhQuoteBtn()}
    </div>
  );

  const renderAiActions = () => (
    <>
      {renderCopyButton()}
    </>
  );

  // Tin nhắn trả lời thường của AI: sao chép, đọc to, like, dislike
  const renderAiReplyActions = () => (
    <>
      {renderCopyButton()}
      {renderVolumeButton()}
      {renderLikeButton()}
      {renderDislikeButton()}
    </>
  );

  const renderActionButtons = () => (isCskhMode ? renderCskhActions() : renderAiActions());

  const renderStoppedWarning = () => {
    if (!isStopped && !chatMessage?.isStopped) return null;
    return (
      <div className="stop-message-warning">
        Bạn đã dừng trả lời câu hỏi này
      </div>
    );
  };

  const renderCitation = () => {
    const isDone = isTypingComplete || isStopped || chatMessage?.isStopped;
    if (!isDone || !chatMessage?.references?.length) return null;
    return <CitationButton references={chatMessage.references} />;
  };

  const renderFollowUps = () => {
    const isDone = isTypingComplete && !isStopped && !chatMessage?.isStopped;
    if (!isDone || !chatMessage?.followUpQuestions?.length || chatMessage?._isHistory) return null;
    return (
      <FollowUpSuggestions
        questions={chatMessage.followUpQuestions}
        onQuestionClick={handleQuestionClick}
        disabled={!isLastMessage}
      />
    );
  };

  const renderSuggestionButtons = () => {
    const isVisible = isTypingComplete && !isStopped && !chatMessage?.isStopped &&
      !isFromHistory && isLastMessage && !isOrderMessage && !isProductListMessage &&
      !paymentAccount && !isReportMessage && !isCompleteMessage &&
      chatMessage?.buttons && chatMessage?.buttons.length > 0;
    if (!isVisible) return null;

    return (
      <div className="flex flex-wrap gap-2 mt-3 mb-2">
        {chatMessage?.buttons?.map((button: any, index: number) => {
          const isDisabled = button.enabledColor === null;
          return (
            <button
              key={button.id || index}
              className={`px-4 py-1.5 rounded-[8px] border text-sm font-medium shadow-sm ${isDisabled ? '' : 'hover:opacity-80'}`}
              style={{
                transition: isDisabled ? 'none' : 'all 0.3s ease',
                cursor: isDisabled ? 'default' : 'pointer',
                borderColor: isDisabled ? '#E7EAF3B2' : 'var(--colour-neutral-surface)',
                color: isDisabled ? '#b1b1b1ff' : 'var(--colour-primary-shade-md)',
                backgroundColor: isDisabled ? '#E7EAF3B2' : 'var(--colour-primary-light-xxl)',
                ...getButtonStyle(button, isDisabled),
              }}
              onClick={() => !isDisabled && onButtonClick?.(button.name, button.id, checkIsHidden(button.commandVariables))}
              type="button"
              title={isFromHistory ? 'Tin nhắn từ lịch sử' : ''}
            >
              {button.name}
            </button>
          );
        })}
      </div>
    );
  };

  // Tin nhắn AI: hover → hiện giờ ở mép phải, đi theo con trỏ theo chiều dọc (CSS var đọc ở ChatAdmin.scss)
  const handleMessageHoverMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
    e.currentTarget.style.setProperty('--msg-time-top', `${y}px`);
  };

  const renderMessageHoverTime = () => {
    if (!formattedTime) return null;
    return (
      <span className="chat-admin__msg-time" data-testid="ai-msg-time" aria-hidden="true">
        {formattedTime}
      </span>
    );
  };

  const renderBubbleExtras = () => (
    <>
      {renderStoppedWarning()}
      {renderCitation()}
      {renderSuggestionButtons()}
      {renderFollowUps()}
    </>
  );

  const renderGalleryRow = (alignStart: boolean) => {
    if (!galleryImageUrls.length) return null;
    const tooltipProps = (isImageOnly && shouldShowTimeTooltip) ? {
      'data-tooltip-id': 'cskh-msg-time-tooltip',
      'data-tooltip-content': formattedTime,
      'data-tooltip-place': 'left' as const,
    } : {};
    return (
      <div className={`chat-admin__gallery-row${!isImageOnly ? ' mb-2' : ''} w-full flex justify-start items-center gap-2`} {...tooltipProps}>
        <ImageGallery imageUrls={galleryImageUrls} align={alignStart ? 'start' : undefined} />
        {isImageOnly && showCskhBubble && (isTypingComplete || isStopped || chatMessage?.isStopped) && renderCskhActions()}
      </div>
    );
  };

  const renderCskhBubbleRow = () => {
    if (isImageOnly || (!hasValidText && !isSpecialCard)) return null;
    const timeProps = shouldShowTimeTooltip ? {
      'data-tooltip-id': 'cskh-msg-time-tooltip',
      'data-tooltip-content': formattedTime,
      'data-tooltip-place': 'left' as const,
    } : {};
    return (
      <div className="chat-admin__bubble-row flex items-center gap-2 max-w-full">
        <div className={`border-chat-bot break-words border-chat-bot--cskh ${groupPosition ? `border-chat-bot--cskh-${groupPosition}` : ''} ${isProductListMessage ? 'transparent-card' : ''} ${isOrderMessage ? 'bg-transparent border-none p-0' : ''} ${isReportMessage ? 'full-width' : ''}`} ref={messageContentRef} {...timeProps}>
          {renderContent()}
          {renderBubbleExtras()}
        </div>
        {(isTypingComplete || isStopped || chatMessage?.isStopped) && hasValidText && renderCskhActions()}
      </div>
    );
  };

  const renderAiBubbleRow = () => {
    if (isImageOnly || (!hasValidText && !isSpecialCard)) return null;
    return (
      <div className="chat-admin__msg-hover" onMouseMove={handleMessageHoverMove}>
        <div
          className={`border-chat-bot break-words ${isProductListMessage ? 'transparent-card' : ''} ${isOrderMessage ? 'bg-transparent border-none p-0' : ''} ${isReportMessage ? 'full-width' : ''}`}
          ref={messageContentRef}
          {...(shouldShowTimeTooltip && {
            'data-tooltip-id': 'cskh-msg-time-tooltip',
            'data-tooltip-content': formattedTime,
            'data-tooltip-place': 'left' as const,
          })}
        >
          {renderContent()}
          {renderBubbleExtras()}
        </div>
        {renderMessageHoverTime()}
      </div>
    );
  };

  return (
    <React.Fragment>
      {/* Confirm-switch message type */}
      {chatMessage?.confirmSwitchAgent && (
        <div className={`chat-admin flex flex-col mt-[10px]${isCskhMode ? ' chat-admin--cskh' : ''}`}>
          <div className="flex mb-2 text-purple-400">
            <ThinkingIcon className={`w-5 h-5 ${(isLastMessage && !chatMessage?._isHistory) ? 'animate-pulse' : ''}`} />
          </div>
          <div className="flex items-center w-full">
            <div className="flex flex-col w-full">
              <div
                className="border-chat-bot break-words"
                {...(shouldShowTimeTooltip && {
                  'data-tooltip-id': 'cskh-msg-time-tooltip',
                  'data-tooltip-content': formattedTime,
                  'data-tooltip-place': 'left' as const,
                })}
              >
                <ConfirmSwitchMessage
                  messageId={chatMessage.id || chatMessage.messageId || ''}
                  pendingAgent={chatMessage.confirmSwitchAgent}
                  isDisabled={!!chatMessage.isConfirmDisabled}
                  originalBlockContents={chatMessage.originalBlockContents}
                  onStay={(id, bc) => onConfirmStay?.(id, bc)}
                  onLeave={(id, agent) => onConfirmLeave?.(id, agent)}
                />
              </div>
              <div className={`chat-admin-actions flex items-center justify-start font-size-time ${isCskhMode ? 'chat-admin-actions--cskh mt-1' : 'gap-2 mt-3'}`}>
                {renderActionButtons()}
              </div>
            </div>
          </div>
        </div>
      )}

      {!chatMessage?.confirmSwitchAgent && (
        <>
          {showToastMessage && (
            <div className="copy-toast">
              <div className="copy-toast-content">
                <CopyIconSuccessCircle />
                {t(getToastMessage())}
              </div>
            </div>
          )}

          <div className={`chat-admin flex flex-col mt-[10px]${isCskhMode ? ' chat-admin--cskh' : ''}`}>
            {/* <div className="flex mb-2 text-purple-400">
              <ThinkingIcon className={`w-5 h-5 ${(isLastMessage && !chatMessage?._isHistory) ? 'animate-pulse' : ''}`} />
            </div> */}
            <div className={`flex w-full ${showCskhBubble ? `${getCskhAvatarAlignClass(groupPosition)} gap-3` : 'items-center'}`}>
              {showCskhBubble && (
                <div
                  className={`chat-admin__avatar${(groupPosition === 'first' || groupPosition === 'middle') ? ' invisible' : ''}`}
                  aria-hidden
                >
                  <CskhBotMsgIcon className="w-10 h-10" />
                </div>
              )}
              <div className={`flex flex-col ${showCskhBubble ? 'min-w-0 max-w-full' : 'w-full'}`}>
                {showCskhBubble ? (
                  <>
                    {renderGalleryRow(true)}
                    {renderCskhBubbleRow()}
                  </>
                ) : (
                  <>
                    {renderGalleryRow(true)}
                    {renderAiBubbleRow()}
                    {(isTypingComplete || isStopped || chatMessage?.isStopped) && hasValidText && (
                      <div className="chat-admin-actions flex items-center justify-start mt-1 font-size-time">
                        {renderAiReplyActions()}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {showDislikeBlock && chatMessage?.id && (
        <DislikeBlock
          onCloseDislikeBlock={handleDislikeBlockClose}
          onSubmitReason={handleDislikeSubmitReason}
          onShowToast={handleShowDislikeToast}
        />
      )}
    </React.Fragment>
  );
};

export default React.memo(ChatAdmin, (prevProps, nextProps) => {
  return (
    prevProps.messageId === nextProps.messageId &&
    prevProps.message === nextProps.message &&
    prevProps.isLastMessage === nextProps.isLastMessage &&
    prevProps.activeAgent === nextProps.activeAgent &&
    prevProps.chatMessage === nextProps.chatMessage &&
    prevProps.isBotSpeaking === nextProps.isBotSpeaking &&
    prevProps.isAudioPlayerVisible === nextProps.isAudioPlayerVisible &&
    prevProps.isAudioEnabled === nextProps.isAudioEnabled &&
    prevProps.audioData === nextProps.audioData &&
    prevProps.groupPosition === nextProps.groupPosition
  );
});
